import { memo, MouseEventHandler, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import ServiceConnectionStep from "components/ServiceConnectionStep";

// -----------------------------------------------------------------------------

const OktaDomainRegex = /^(https:\/\/[\w-].+\.okta\.com)/;

// -----------------------------------------------------------------------------

const ConfiguringOktaPhase = memo(function ConfiguringOktaPhase({
  disabled,
  organization,
  apiToken,
  mode,
  onChangeOrganization,
  onChangeApiToken,
  onChangeMode,
  onNext,
}: TestingConnectionProps): JSX.Element {
  const [errorOrganization, setErrorOrganization] = useState("");
  const [errorApiToken, setErrorApiToken] = useState("");
  const [adminUrl, setAdminUrl] = useState(false);

  //
  return (
    <ServiceConnectionStep disabled={disabled}>
      <Stack spacing={1.5} direction="column">
        <Typography variant="body1">
          Please provide the <strong>Okta organization URL</strong> and the{" "}
          <strong>API token</strong> associated with the account you&apos;d like
          to connect to ThreatKey.
        </Typography>
        <TextField
          fullWidth
          label="Okta Organization URL"
          placeholder="https://your-org-name.okta.com"
          sx={{ pt: 0.5 }}
          value={organization}
          onChange={(event) => {
            const newOrganization = event.target.value.trim();
            const orgURLValid = !(
              newOrganization.match(OktaDomainRegex) == null
            );

            onChangeOrganization(event.target.value);
            setErrorOrganization(orgURLValid ? "" : "Invalid OKTA URL");

            setAdminUrl(newOrganization.includes("-admin.okta.com"));
          }}
          error={errorOrganization !== ""}
          helperText={errorOrganization}
          data-private="lipsum"
        />
        <TextField
          fullWidth
          label="API Token"
          placeholder="00q2vRRmTYDHid5..."
          value={apiToken}
          onChange={(event) => {
            onChangeApiToken(event.target.value);
            setErrorApiToken("");
          }}
          onBlur={() => {
            setErrorApiToken(apiToken.trim() === "" ? "Invalid API Token" : "");
          }}
          error={errorApiToken !== ""}
          helperText={errorApiToken}
          data-private="lipsum"
        />
        <FormControl sx={{ pt: 1 }}>
          <FormLabel>App Permissions</FormLabel>
          <RadioGroup
            value={mode}
            onChange={(event) => onChangeMode(event.target.value)}
          >
            <FormControlLabel
              value="readwrite"
              control={<Radio />}
              label="Read-write permissions"
            />
            <FormControlLabel
              value="readonly"
              control={<Radio />}
              label="Read-only permissions (disables fixes)"
            />
          </RadioGroup>
        </FormControl>
        <Typography variant="body1">
          This app can see assets in this Okta organization and its security
          logs. It will periodically re-scan your assets to react to changes to
          Okta itself. The app will have permission to re-provision itself in
          response to Okta introducing new features and asset types.
        </Typography>

        {adminUrl ? (
          <Alert severity="warning">
            You are using the Okta admin URL, we will remove the
            &apos;-admin&apos; from your URL on connection.
          </Alert>
        ) : null}
      </Stack>

      <Box sx={{ flexGrow: 0, mt: 2.5 }}>
        <Tooltip
          arrow
          title={
            !organization || !apiToken || !!errorOrganization
              ? "Please fill the form out correctly"
              : ""
          }
        >
          <span>
            <Button
              disabled={!organization || !apiToken || !!errorOrganization}
              variant="contained"
              onClick={onNext as MouseEventHandler<HTMLButtonElement>}
            >
              Connect to Okta
            </Button>
          </span>
        </Tooltip>
      </Box>
    </ServiceConnectionStep>
  );
});

export default ConfiguringOktaPhase;

// -----------------------------------------------------------------------------

interface TestingConnectionProps {
  disabled: boolean;
  organization: string;
  apiToken: string;
  mode: "readwrite" | "readonly";
  onChangeOrganization: Function;
  onChangeApiToken: Function;
  onChangeMode: Function;
  onNext: Function;
}
